import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '../ProductCard/ProductCard';
import { products, categories } from '../../data/products';
import { useProducts } from '../../context/ProductContext';
import './Shop.css';

const ITEMS_PER_PAGE = 12;

const Shop = () => {
  const [searchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { selectedCategory, setSelectedCategory, priceRange, setPriceRange, sortBy, setSortBy, clearFilters } = useProducts();
  const searchQuery = searchParams.get('search') || '';
  const brandQuery = searchParams.get('brand') || '';
  // Scroll to top when component mounts or search params change
useEffect(() => {
  window.scrollTo(0, 0);
}, [searchQuery]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(query) || p.brand.toLowerCase().includes(query) || p.category.toLowerCase().includes(query));
    }

    if (brandQuery) {
  result = result.filter((p) => p.brand.toLowerCase() === brandQuery.toLowerCase());
}
    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category.toLowerCase() === selectedCategory.toLowerCase());
    }
    result = result.filter((p) => {
      const price = p.salePrice || p.price;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price)); break;
      case 'price-high': result.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price)); break;
      case 'rating': result.sort((a, b) => b.rating - a.rating); break;
      default: break;
    }
    return result;
  }, [searchQuery, brandQuery, selectedCategory, priceRange, sortBy]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [selectedCategory, priceRange, sortBy, searchQuery, brandQuery]);

  const priceRanges = [['Under $25', 0, 25], ['$25 - $50', 25, 50], ['$50 - $100', 50, 100], ['$100 - $200', 100, 200], ['Over $200', 200, 500]];

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1 className="shop-title">{brandQuery ? `${brandQuery} Products` : searchQuery ? `Search: "${searchQuery}"` : 'Shop All'}</h1>
        <p className="shop-count">{filteredProducts.length} products</p>
      </div>

      <button className="shop-filter-toggle" onClick={() => setIsFilterOpen(!isFilterOpen)}>
        <SlidersHorizontal size={18} />
        {isFilterOpen ? 'Close Filters' : 'Show Filters'}
      </button>

      <div className="shop-container">
        <aside className={`shop-sidebar ${isFilterOpen ? 'open' : ''}`}>
          <div className="shop-filter-section">
            <h4 className="shop-filter-title">Category</h4>
            <div className="shop-filter-options">
              <label className="shop-filter-option"><input type="radio" name="category" checked={selectedCategory === 'all'} onChange={() => setSelectedCategory('all')} />All Categories</label>
              {categories.map((cat) => (
                <label key={cat.id} className="shop-filter-option"><input type="radio" name="category" checked={selectedCategory === cat.id} onChange={() => setSelectedCategory(cat.id)} />{cat.name}</label>
              ))}
            </div>
          </div>

          <div className="shop-filter-section">
            <h4 className="shop-filter-title">Price Range</h4>
            <div className="shop-filter-options">
              {priceRanges.map(([label, min, max]) => (
                <label key={label} className="shop-filter-option"><input type="radio" name="price" checked={priceRange[0] === min && priceRange[1] === max} onChange={() => setPriceRange([min, max])} />{label}</label>
              ))}
            </div>
          </div>

          <button className="shop-clear-filters" onClick={clearFilters}>Clear All Filters</button>
        </aside>

        <div className="shop-products">
          <div className="shop-toolbar">
            <div>Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length}</div>
            <select className="shop-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>

          <div className="shop-products-grid">
            {paginatedProducts.length > 0 ? paginatedProducts.map((product) => <ProductCard key={product.id} product={product} />) : (
              <div className="shop-empty"><h3>No products found</h3><p>Try adjusting your filters.</p></div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="shop-pagination">
              <button className="shop-pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i + 1} className={`shop-pagination-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="shop-pagination-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {isFilterOpen && <div className="cart-drawer-overlay open" onClick={() => setIsFilterOpen(false)} />}
    </div>
  );
};

export default Shop;
